import React, { useState, useEffect } from 'react';
import { UseConvertZonedDateTime } from '../../hooks/UseConvertZonedDateTime';
import UseYearMonthDateShort from '../../hooks/UseYearMonthDateShort';
import classes from "./TimeDatePciker.module.css";

const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const getCurrentDate = () => {
    const newDate = new Date();
    const startDate = UseYearMonthDateShort(newDate);
    const maxDate = UseYearMonthDateShort(newDate.setUTCFullYear(newDate.getUTCFullYear() + 1));
    return { startDate, minDate: startDate, maxDate };
};

// timeZone is the IANA zone the value should be stored in - the schedule's original timezone when
// editing an existing item, or the viewer's own timezone by default when creating a new one
const TimeDatePicker = ({ handler, label, preSelectedDateTime, timeZone }) => {
    const [time, setTime] = useState(getCurrentTime);
    const [date, setDate] = useState(getCurrentDate);

    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const storageTimeZone = timeZone || browserTimeZone;

    const buildStoredDateTime = (dateStr, timeStr) => {
        const localDateTime = `${dateStr} ${timeStr}:00`;
        return UseConvertZonedDateTime(localDateTime, browserTimeZone, storageTimeZone);
    };

    // Handle time changes
    const handleTimeChange = (event) => {
        const selectedTime = event.target.value;
        setTime(selectedTime);

        if (selectedTime && date) {
            handler(buildStoredDateTime(date.startDate, selectedTime));
        }
    };

    // Handle date changes
    const handleDateChange = (event) => {
        const selectedDate = {
            startDate: event.target.value,
            minDate: date.minDate,
            maxDate: date.maxDate,
        };
        setDate(selectedDate);

        if (time && selectedDate.startDate) {
            handler(buildStoredDateTime(selectedDate.startDate, time));
        }
    };

    useEffect(() => {
        const initialTime = getCurrentTime();
        const initialDate = getCurrentDate();

        if (preSelectedDateTime) {
            // convert the stored value (in storageTimeZone) into the viewer's local timezone for editing
            const localDateTime = UseConvertZonedDateTime(preSelectedDateTime, storageTimeZone, browserTimeZone);
            const [datePart, timePart = '00:00'] = String(localDateTime).trim().replace('T', ' ').split(' ');

            const newDate = new Date();
            const minDate = newDate.toISOString().split('T')[0]; // Get current date in 'YYYY-MM-DD' format

            // Add one year to the Date object
            newDate.setFullYear(newDate.getFullYear() + 1);

            // Format the updated Date object back to 'YYYY-MM-DD'
            const year = newDate.getFullYear();
            const month = String(newDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
            const day = String(newDate.getDate()).padStart(2, '0');
            const maxDate = `${year}-${month}-${day}`;

            const dateObject = {
                startDate: datePart,
                minDate,
                maxDate
            };

            setTime(timePart.slice(0, 5));
            setDate(dateObject);
        } else {
            setTime(initialTime);
            setDate(initialDate);
            handler(buildStoredDateTime(initialDate.startDate, initialTime));
        }

    }, []);

    return (
        <div className={classes["container"]}>
            <label htmlFor="appt">{label || "Select Date and Time on:"}</label>

            <input
                type="time"
                id="timePicker"
                name="holiday-timePicker"
                value={time}
                onChange={handleTimeChange}
                className={classes["time-input"]}
            />

            <label htmlFor="appt">{"on the"}</label>

            <input
                type="date"
                id="start"
                className={classes["time-input"]}
                name="holiday-start"
                value={date && date.startDate ? date.startDate : ""}
                onChange={handleDateChange}
                min={date && date.minDate ? date.minDate : ""}
                max={date && date.maxDate ? date.maxDate : ""}
            />
        </div>
    );
};

export default TimeDatePicker;
